<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon> <CloseLogo></CloseLogo> </template>
                <DialogTitle>
                    {{
                        integration
                            ? $t("integrations.close.modal.title_edit")
                            : $t("integrations.close.modal.title_add")
                    }}</DialogTitle
                >
            </DialogHeader>
            <div class="px-6 sm:mt-0 sm:text-left w-full text-center">
                <h3
                    id="modal-title"
                    class="text-lg font-medium leading-6 text-iron-900"
                ></h3>
                <div class="mt-2">
                    <div class="mt-4">
                        <div class="gap-1.5 grid w-full items-center">
                            <Label for="close-name">{{
                                $t("integrations.close.modal.nameLabel")
                            }}</Label>
                            <Input
                                v-model="form.name"
                                id="close-name"
                                :placeholder="
                                    $t(
                                        'integrations.close.modal.name_placeholder',
                                    )
                                "
                            />
                            <p
                                v-if="form.errors.name"
                                class="text-sm text-red-600"
                            >
                                {{ form.errors.name }}
                            </p>
                        </div>
                    </div>

                    <div class="mt-4">
                        <div class="gap-1.5 grid w-full items-center">
                            <Label for="close-apikey">{{
                                $t("integrations.close.modal.apiKeyLabel")
                            }}</Label>
                            <Input
                                v-model="form.apiKey"
                                id="close-apikey"
                                :placeholder="
                                    $t(
                                        'integrations.close.modal.api_key_placeholder',
                                    )
                                "
                            />
                            <p
                                v-if="form.errors.apiKey"
                                class="text-sm text-red-600"
                            >
                                {{ form.errors.apiKey }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <DialogFooter
                class="w-full"
                :class="cn(removable ? '!justify-between' : 'justify-end')"
            >
                <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>
                <div class="flex-end flex">
                    <DialogClose>
                        <Button class="ml-4" variant="outline" type="button">{{
                            $t("cancel")
                        }}</Button>
                    </DialogClose>

                    <Button
                        class="ml-4"
                        type="button"
                        :disabled="form.processing"
                        @click="createOrUpdateCloseIntegration"
                        >{{
                            integration ? $t("save_changes") : $t("add")
                        }}</Button
                    >
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { cn } from "@/lib/utils";

import { ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import { Trash } from "lucide-vue-next";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import CloseLogo from "../logos/simple/Close.svg";
import route from "ziggy-js";

const props = defineProps<{
    removable?: boolean;
    integration?: any;
}>();

const opened = ref(false);

const form = useForm({
    name: props.integration?.name || "",
    apiKey: props.integration?.data?.apiKey || "",
});

function create() {
    form.post(route("app.close-integrations.store"), {
        // Keep the dialogs open only to show validation errors; on success the
        // page re-mounts so every integration dialog closes.
        preserveState: "errors",
        onSuccess: () => closeModal(),
    });
}

function update() {
    form.put(
        route("app.close-integrations.update", {
            integration: props.integration.id,
        }),
        {
            preserveState: "errors",
            onSuccess: () => closeModal(),
        },
    );
}

function createOrUpdateCloseIntegration() {
    if (props.integration) {
        update();
    } else {
        create();
    }
}

const closeModal = () => {
    opened.value = false;
};
</script>

<style></style>
